import React, { useState, useEffect } from 'react';
import { requestNotificationPermission, registerTokenWithBackend, onForegroundMessage } from '../firebase';
import authFetch from '../utils/authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

function NotificationButton({ userId }) {
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [, setDaysSinceInstall] = useState(0); // kept for potential future re-gating

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setIsSupported(false);
      return;
    }
    setPermissionStatus(Notification.permission);
    const registered = localStorage.getItem('push-registered');
    if (registered === 'true') setIsRegistered(true);
    // Explicitly declined (onboarding "Not now" / Profile switch off): no nagging banner — Profile manages it.
    if (localStorage.getItem(`notif-declined-${userId}`) === 'true') setIsRegistered(true);

    // Track first app visit for the 3-day delay rule
    if (!localStorage.getItem('app_first_visit')) {
      localStorage.setItem('app_first_visit', Date.now().toString());
    }
    const firstVisit = parseInt(localStorage.getItem('app_first_visit') || '0', 10);
    setDaysSinceInstall((Date.now() - firstVisit) / 86400000);

    // Foreground messages: FCM routes them here (NOT the SW's onBackgroundMessage)
    // when the app is open+focused. The backend sends DATA-ONLY messages (no
    // `notification` field — 2026-05-31 double-bubble fix), so payload.notification
    // is undefined. Reading it here silently DROPPED every foreground push (user
    // report 2026-07-11: no push at all when the run synced while the app was open).
    // Read title/body from payload.data (same source the SW uses) and render a REAL
    // notification via the SW registration — consistent with the background bubble.
    onForegroundMessage(async (payload) => {
      const data = payload.data || {};
      const title = data.title || payload.notification?.title || 'RitmoIQ 🏃';
      const body  = data.body  || payload.notification?.body  || 'יש לך עדכון חדש';
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          // The FCM service worker (firebase-messaging-sw.js) owns notificationclick. A bare getRegistration()
          // returns the PWA cache worker (/sw.js, scope '/') which has NO click handler — tapping a foreground
          // notification did nothing. Prefer the FCM registration; fall back to any registration.
          const reg = (await navigator.serviceWorker?.getRegistration('/firebase-cloud-messaging-push-scope'))
            || (await navigator.serviceWorker?.getRegistration());
          if (reg) {
            reg.showNotification(title, {
              body, icon: '/logo192.png', badge: '/logo192.png',
              vibrate: [200, 100, 200], dir: 'rtl', lang: 'he',
              tag: `ritmo-${data.type || 'general'}`, renotify: true, data,
            });
          }
        }
      } catch (e) {
        console.warn('Foreground notification render failed:', e);
      }
    });
  }, []);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      if (isRegistered) {
        // כיבוי — מחק token
        await authFetch(`${API_BASE_URL}/unregister-push-token/${userId}`, { method: 'DELETE' });
        localStorage.removeItem('push-registered');
        localStorage.removeItem('fcm-token');
        setIsRegistered(false);
      } else {
        // הפעלה — בקש token חדש
        const token = await requestNotificationPermission();
        if (token) {
          const success = await registerTokenWithBackend(token, userId);
          if (success) {
            setIsRegistered(true);
            setPermissionStatus('granted');
            localStorage.setItem('push-registered', 'true');
            localStorage.setItem('fcm-token', token);
          }
        } else {
          setPermissionStatus(Notification.permission);
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      if (isRegistered) {
        localStorage.removeItem('push-registered');
        setIsRegistered(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Already registered → no banner needed
  if (isRegistered) return null;
  // Unsupported or blocked → silent (don't nag with error banners)
  if (!isSupported || permissionStatus === 'denied') return null;
  // 3-day-delay gate REMOVED (2026-08-25): browser permission and localStorage are
  // per-origin, so the app.ritmoiq.com migration made every existing user look "new"
  // and silently hid push re-registration for 3 days (user report). One quiet A2
  // banner that disappears on enable doesn't need a delay.

  // A2 banner shell (2026-08-25 redesign): cyan semantic accent, SVG bell, no emoji.
  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      style={{
        width: '100%', borderRadius: 16, padding: '13px 14px',
        background: '#1e293b', border: '1px solid rgba(56,189,248,.28)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)', cursor: isLoading ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 12, textAlign: 'right',
        opacity: isLoading ? .6 : 1,
      }}
    >
      <span style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: 'rgba(56,189,248,.12)', border: '1px solid rgba(56,189,248,.22)',
        color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 9a6 6 0 1 0-12 0c0 6-2.5 7-2.5 7h17S18 15 18 9z" />
          <path d="M10.3 20a2 2 0 0 0 3.4 0" />
        </svg>
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: "'Heebo',sans-serif", fontWeight: 700, fontSize: 14, color: '#f8fafc' }}>
          {isLoading ? 'מפעיל התראות…' : 'הפעל התראות'}
        </span>
        <span style={{ display: 'block', fontFamily: "'Heebo',sans-serif", fontWeight: 500, fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>
          עדכונים על אימונים, תכניות והתאוששות
        </span>
      </span>
    </button>
  );
}

export default NotificationButton;
